import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAppVersion } from '@/utils/version';
import { useServicesQuery } from '@/services/api';

const FETCH_INTERVAL = 60 * 1000; // 1 minute

export interface ReleaseVersionData {
  major: number;
  minor: number;
}

export function parseVersion(version: string): ReleaseVersionData {
  // Version format: "1.2.3+commitHash" — extract the semver part before the "+"
  const semver = version.split('+')[0] ?? '';
  const parts = semver.split('.');
  const major = Number(parts[0] ?? 0);
  const minor = Number(parts[1] ?? 0);
  return {
    major: Number.isNaN(major) ? 0 : major,
    minor: Number.isNaN(minor) ? 0 : minor,
  };
}

/**
 * Polls the releases API every minute and shows a modal when the app version
 * has a major or minor bump relative to the version loaded at page load.
 *
 * - Major bump: shows modal immediately on the next poll
 * - Minor bump: shows modal on the next route transition
 * - Patch bump: ignored
 * - Rollbacks (lower version): ignored
 */
export function useReleaseNotification() {
  const originalVersion = useRef(parseVersion(getAppVersion()));
  const [showModal, setShowModal] = useState(false);
  const majorUpgradeShown = useRef(false);
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);

  const { data: current } = useServicesQuery(
    'getReleaseSummary',
    {},
    {
      refetchInterval: FETCH_INTERVAL,
      retry: false,
      throwOnError: false,
      select: (data) => parseVersion(data.hydra.version),
    },
  );

  useEffect(() => {
    // Always update the ref so navigations before the first poll response don't
    // look like a route change when current eventually arrives.
    const isRouteChange = location.pathname !== previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    if (current == null) return;

    const original = originalVersion.current;
    const hasMajorBump = current.major > original.major;
    // Guard minor check against major rollbacks (e.g. 1.2 → 0.9 where 9 > 2)
    const hasMinorBump = current.major === original.major && current.minor > original.minor;

    if (hasMajorBump && !majorUpgradeShown.current) {
      majorUpgradeShown.current = true;
      setShowModal(true);
    } else if (isRouteChange && (hasMajorBump || hasMinorBump)) {
      setShowModal(true);
    }
  }, [current, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps -- refs are intentionally excluded

  const reload = () => {
    setShowModal(false);
    window.location.reload();
  };

  return { showModal, reload };
}
